'use client';

import { ErrorBoundary } from '@sentry/nextjs';
import type { ChangeEvent} from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Close, Search } from '@zoomit/icons';
import { Container, IconButton, Link, useDebounce } from '@zoomit/primitives';
import { onSearchTerm, OriginURL, SomethingWentWrong } from '@zoomit/web-kit';
import { cssVar, Input, theme } from '@zoomit/zoomit-ds';

import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';
import type { MenuParserData } from '../../../types/menus';
import { SearchInstance } from '../SearchInstance';
import MenuAvatarMobile from './DefaultMenuMobileAvatar';

type Props = {
  onCloseMenu(): void;
  searchInstanceRenderer?: MenuParserData['searchInstanceRenderer'];
};

const DefaultMenuSearchRouteMobile = (props: Props) => {
  const { onCloseMenu, searchInstanceRenderer } = props;
  const navigate = useNavigate();
  const basePath = useWebKitCustomization((settings) => settings.basePath);
  const tenantLogoRenderer = useWebKitCustomization((settings) => settings.tenantLogoRenderer);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (event.target.value.length !== 0) onSearchTerm(event.target.value);
  };

  return (
    <>
      <header
        className={'bg-background2 sticky top-0 flex w-full items-center justify-between px-4 py-[13px]'}
      >
        <IconButton onClick={onCloseMenu}>
          <Close fill={cssVar(theme.grey3)} size="xl" />
        </IconButton>

        <Link href={`${OriginURL}/${basePath}`} style={{ display: 'flex' }}>
          {tenantLogoRenderer({ fill: cssVar(theme.text) })}
        </Link>

        <MenuAvatarMobile Logo={() => tenantLogoRenderer({ fill: cssVar(theme.text) })} />
      </header>

      <div className={'h-[calc(100vh-58px)] px-0 py-4'}>
        <ErrorBoundary fallback={<SomethingWentWrong />}>
          <Container
            className={'xxl:max-w-[872px] h-full py-0 md:pt-12 md:pb-6'}
            spacing={{ pt: 'x12', px: 'x6', pb: 'x6' }}
          >
            <Input
              name="search"
              variant="rounded"
              rightNode={<Search fill={cssVar(theme.text)} />}
              placeholder={'جستجو ...'}
              value={searchQuery}
              onChange={handleSearch}
              autoFocus
            />
            {searchInstanceRenderer ? (
              searchInstanceRenderer({
                searchQuery: debouncedSearchQuery,
                onClose: () => {
                  navigate('/home');
                  onCloseMenu();
                },
              })
            ) : (
              <SearchInstance
                searchQuery={debouncedSearchQuery}
                onClose={() => {
                  navigate('/home');
                  onCloseMenu();
                }}
                {...props}
              />
            )}
          </Container>
        </ErrorBoundary>
      </div>
    </>
  );
};

export default DefaultMenuSearchRouteMobile;
